import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Search, Plus, Building2, AlertTriangle, Package, Lock, Pencil } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

const REGIONS = ['National', 'Remote', 'Very Remote'];
const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${Number(v).toFixed(2)}`);

const apiError = (e, fallback) => {
  const d = e?.response?.data;
  return d?.detail?.error?.message || d?.error?.message || d?.detail?.message || (typeof d?.detail === 'string' ? d.detail : null) || fallback;
};

export default function BranchCatalogue() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('services.manage');

  const [branches, setBranches] = useState([]);
  const [branchCode, setBranchCode] = useState('');
  const [enabled, setEnabled] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add-from-NDIS state
  const [addOpen, setAddOpen] = useState(false);
  const [availQuery, setAvailQuery] = useState('');
  const [avail, setAvail] = useState([]);
  const [availLoading, setAvailLoading] = useState(false);
  const [availTotal, setAvailTotal] = useState(0);

  // Config dialog for a selected master item
  const [configItem, setConfigItem] = useState(null); // master item to enable
  const [region, setRegion] = useState('National');
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit-rate dialog
  const [rateItem, setRateItem] = useState(null);
  const [rateValue, setRateValue] = useState('');

  const loadBranches = async () => {
    try {
      const res = await api.get('/org/branches');
      const list = res.data.data || [];
      setBranches(list);
      if (list.length && !branchCode) setBranchCode(list[0].code);
    } catch { toast.error('Failed to load branches'); }
  };

  const loadEnabled = async (code) => {
    if (!code) return;
    setLoading(true); setError(null);
    try {
      const res = await api.get('/branch-catalogue', { params: { branch_id: code } });
      setEnabled(res.data.data || []);
    } catch (e) {
      setError(apiError(e, 'Failed to load branch catalogue.'));
    } finally { setLoading(false); }
  };

  const loadAvailable = async (code, q) => {
    if (!code) return;
    setAvailLoading(true);
    try {
      const res = await api.get('/branch-catalogue/available', { params: { branch_id: code, q: q || undefined, pageSize: 50 } });
      setAvail(res.data.data || []);
      setAvailTotal(res.data.total || 0);
    } catch (e) { toast.error(apiError(e, 'Failed to load available items')); }
    finally { setAvailLoading(false); }
  };

  useEffect(() => { loadBranches(); }, []);
  useEffect(() => { loadEnabled(branchCode); }, [branchCode]);

  const openAdd = () => { setAddOpen(true); setAvailQuery(''); loadAvailable(branchCode, ''); };

  const pickItem = (it) => {
    setConfigItem(it);
    setRegion('National');
    setRate(it.unit_price_one ? '1' : '');
  };

  const regionCap = useMemo(() => {
    if (!configItem) return null;
    const c = configItem.price_caps?.[region];
    return c === null || c === undefined || c === '' ? null : Number(c);
  }, [configItem, region]);

  const isQuote = configItem && (configItem.quote || configItem.support_type === 'Quotable Supports');
  const isUnitOne = configItem && configItem.unit_price_one;
  const rateNum = rate === '' ? null : Number(rate);
  const rateOverCap = regionCap != null && rateNum != null && rateNum > regionCap;

  const confirmEnable = async () => {
    if (!configItem) return;
    setSaving(true);
    try {
      await api.post('/branch-catalogue/enable', {
        branch_id: branchCode,
        ndis_item_id: configItem.id,
        pricing_region: region,
        agreed_rate: isUnitOne ? undefined : (rate === '' ? undefined : rate),
      });
      toast.success(`Added ${configItem.support_item_number} to ${branchCode}`);
      setConfigItem(null);
      setAddOpen(false);
      await loadEnabled(branchCode);
    } catch (e) {
      toast.error(apiError(e, 'Failed to enable item'));
    } finally { setSaving(false); }
  };

  const toggleEnabled = async (row, value) => {
    try {
      await api.patch(`/branch-catalogue/${row.id}/enabled`, { enabled: value });
      toast.success(value ? 'Item enabled' : 'Item disabled (kept for history)');
      await loadEnabled(branchCode);
    } catch (e) { toast.error(apiError(e, 'Failed to update')); }
  };

  const saveRate = async () => {
    if (!rateItem) return;
    try {
      await api.patch(`/branch-catalogue/${rateItem.id}/rate`, { agreed_rate: rateValue === '' ? undefined : rateValue });
      toast.success('Agreed rate updated');
      setRateItem(null);
      await loadEnabled(branchCode);
    } catch (e) { toast.error(apiError(e, 'Failed to update rate')); }
  };

  if (!canManage) {
    return (
      <div className="p-10 text-center" data-testid="branch-catalogue-denied">
        <Lock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">You do not have permission to manage the branch catalogue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="branch-catalogue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">Branch Catalogue</h1>
          </div>
          <p className="text-muted-foreground mt-1">Enable official NDIS items per branch, set agreed rates (capped), and manage availability. Custom catalogue items are managed separately.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-56">
            <Select value={branchCode} onValueChange={setBranchCode}>
              <SelectTrigger data-testid="branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.code} value={b.code}>{b.name} ({b.code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={openAdd} disabled={!branchCode} data-testid="add-from-ndis-button"><Plus className="h-4 w-4 mr-2" />Add from NDIS Catalogue</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : error ? (
            <div className="p-10 text-center" data-testid="branch-error-state">
              <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-2" />
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => loadEnabled(branchCode)}>Retry</Button>
            </div>
          ) : enabled.length === 0 ? (
            <EmptyState icon={Package} title="No official items for this branch yet" description="Use “Add from NDIS Catalogue” to enable items for this branch." testid="branch-empty-state" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item Number</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Pricing</TableHead>
                  <TableHead className="text-right">Cap</TableHead>
                  <TableHead className="text-right">Agreed Rate</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enabled.map((r) => (
                  <TableRow key={r.id} data-testid={`branch-row-${r.support_item_number}`} className={r.enabled ? '' : 'opacity-60'}>
                    <TableCell className="font-mono text-xs">{r.support_item_number}</TableCell>
                    <TableCell className="max-w-sm"><span className="line-clamp-2">{r.support_item_name}</span></TableCell>
                    <TableCell>{r.pricing_region}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.unit_price_one ? 'Unit Price = $1' : r.is_quotable ? 'Quotable' : 'Priced'}</Badge></TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{money(r.price_cap)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{money(r.agreed_rate)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={!!r.enabled} onCheckedChange={(v) => toggleEnabled(r, v)} data-testid={`branch-toggle-${r.support_item_number}`} />
                        <span className="text-xs text-muted-foreground">{r.enabled ? 'Selectable' : 'Unavailable'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {!r.unit_price_one && (
                        <Button variant="ghost" size="sm" onClick={() => { setRateItem(r); setRateValue(r.agreed_rate ?? ''); }} data-testid={`branch-edit-rate-${r.support_item_number}`} title="Edit agreed rate">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add from NDIS Catalogue dialog */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) setConfigItem(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="add-ndis-dialog">
          <DialogHeader>
            <DialogTitle>Add from NDIS Catalogue — {branchCode}</DialogTitle>
            <DialogDescription>Only current official items are selectable. Legacy items cannot be newly enabled.</DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search official current items…" value={availQuery}
              onChange={(e) => setAvailQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadAvailable(branchCode, availQuery); }}
              className="pl-9" data-testid="ndis-available-search" />
          </div>
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>{availLoading ? 'Searching…' : `${availTotal} match(es) — showing up to 50`}</span>
            <Button size="sm" variant="ghost" onClick={() => loadAvailable(branchCode, availQuery)} data-testid="ndis-available-search-btn">Search</Button>
          </div>

          <div className="border rounded-md max-h-72 overflow-y-auto">
            {availLoading ? (
              <div className="p-4 space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
            ) : avail.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground" data-testid="ndis-available-empty">No available items. They may already be enabled for this branch.</p>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Name</TableHead><TableHead>Pricing</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {avail.map((it) => (
                    <TableRow key={it.id} data-testid={`ndis-available-row-${it.support_item_number}`}>
                      <TableCell className="font-mono text-xs">{it.support_item_number}</TableCell>
                      <TableCell className="max-w-xs"><span className="line-clamp-1">{it.support_item_name}</span></TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{it.unit_price_one ? '$1' : it.quote ? 'Quote' : 'Priced'}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => pickItem(it)} data-testid={`ndis-available-add-${it.support_item_number}`}>Add</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Inline configuration for the picked item */}
          {configItem && (
            <div className="border rounded-md p-4 space-y-4 bg-muted/30" data-testid="ndis-config-panel">
              <div>
                <p className="font-medium">{configItem.support_item_number}</p>
                <p className="text-sm text-muted-foreground">{configItem.support_item_name}</p>
                <div className="flex gap-2 mt-1">
                  <Badge variant="outline" className="text-[10px]">{isUnitOne ? 'Unit Price = $1' : isQuote ? 'Quotable' : 'Priced'}</Badge>
                  <Badge variant="outline" className="text-[10px]">Unit: {configItem.unit || '—'}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pricing region *</Label>
                  <Select value={region} onValueChange={setRegion}>
                    <SelectTrigger data-testid="config-region-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{REGIONS.map((rg) => <SelectItem key={rg} value={rg}>{rg}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Applicable cap: <span className="font-mono">{regionCap != null ? `$${regionCap.toFixed(2)}` : 'None (quotable)'}</span></p>
                </div>
                <div className="space-y-2">
                  <Label>Agreed / provider rate {isQuote && !isUnitOne ? '(required for quotes at agreement time)' : ''}</Label>
                  <Input type="number" step="0.01" min="0" value={rate} disabled={isUnitOne}
                    onChange={(e) => setRate(e.target.value)} placeholder={isUnitOne ? 'Fixed at $1' : 'e.g. 80.00'}
                    data-testid="config-rate-input" />
                  {isUnitOne && <p className="text-xs text-muted-foreground">Unit Price = $1 items are fixed at $1.</p>}
                  {rateOverCap && <p className="text-xs text-red-600" data-testid="config-rate-error">Rate exceeds the {region} cap of ${regionCap.toFixed(2)}.</p>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfigItem(null)} data-testid="config-cancel">Cancel</Button>
                <Button onClick={confirmEnable} disabled={saving || rateOverCap} data-testid="config-enable-confirm">{saving ? 'Adding…' : 'Enable for branch'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit-rate dialog */}
      <Dialog open={!!rateItem} onOpenChange={(v) => { if (!v) setRateItem(null); }}>
        <DialogContent className="max-w-sm" data-testid="edit-rate-dialog">
          <DialogHeader>
            <DialogTitle>Edit agreed rate</DialogTitle>
            <DialogDescription>{rateItem?.support_item_number} · {rateItem?.pricing_region} cap {money(rateItem?.price_cap)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Agreed rate</Label>
            <Input type="number" step="0.01" min="0" value={rateValue} onChange={(e) => setRateValue(e.target.value)} data-testid="edit-rate-input" />
            {rateItem?.price_cap != null && rateValue !== '' && Number(rateValue) > Number(rateItem.price_cap) && (
              <p className="text-xs text-red-600">Rate exceeds cap of {money(rateItem.price_cap)}.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateItem(null)}>Cancel</Button>
            <Button onClick={saveRate} disabled={rateItem?.price_cap != null && rateValue !== '' && Number(rateValue) > Number(rateItem.price_cap)} data-testid="edit-rate-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
